import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { isHrOpsPortalView, isHrPortalView, isHrAdminPortalView, isSuperAdminPortalView } from '../utils/hrAccess';
import { useTeamSize } from '../hooks/useTeamSize';
import QrScanner from './QrScanner';
import { punchFromScan } from '../utils/punchScan';

/**
 * The phone home screen: who you are, whether you are clocked in, and a grid
 * of the things you open every day.
 *
 * Shown only on phones (mobile-app.css hides .mhome above 768px); the desktop
 * dashboard carries on underneath it unchanged.
 *
 * The clock button does not punch from here. A punch needs location, maybe a
 * selfie and the office checks — all of which live on the Attendance page —
 * so the button takes you there with the action ready, rather than
 * re-implementing that flow in a second place where it could drift.
 */

const pad = (n) => String(n).padStart(2, '0');

/* Punch ring geometry */
const RING_R = 96;
const RING_C = 2 * Math.PI * RING_R;

/* Straight-line distance between two points on Earth, in metres. */
const metresBetween = (a, b) => {
  const R = 6371000;
  const toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b.lat - a.lat);
  const dLng = toR(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const fenceDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(m >= 10000 ? 0 : 1)} km` : `${Math.round(m / 10) * 10 || 10}m`);

const hhmmss = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};
const hm = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  return `${Math.floor(m / 60)}h ${pad(m % 60)}m`;
};
const clock = (d) => (d ? new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '--');
const shortTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  const d = new Date(); d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

const TILES = [
  /*
   * Attendance opens the log, not the punch screen.
   *
   * Clocking in on a phone happens from the button at the top of this screen,
   * so the punch page had nothing left to offer here — its own copy says to
   * open the site on a phone, which is where you already are. What somebody
   * actually wants from a tile called Attendance is what their day looks like
   * so far. "My logs" as a second tile was the same destination named twice.
   */
  { to: '/attendance-logs', label: 'Attendance', icon: 'ti-clock-hour-4', c: '#2563eb', b: '#e6efff', perm: 'attendance' },
  { to: '/leave', label: 'Leave', icon: 'ti-beach', c: '#0f766e', b: '#ddf6f1', perm: 'leave' },
  { to: '/expenses', label: 'Expenses', icon: 'ti-currency-rupee', c: '#047857', b: '#d9f5e8', perm: 'expenses' },
  { to: '/my-tasks', label: 'My tasks', icon: 'ti-checklist', c: '#c2410c', b: '#ffeedd', perm: 'my_tasks' },
  { to: '/my-tickets', label: 'Tickets', icon: 'ti-ticket', c: '#5b6ee1', b: '#eceeff', perm: 'tickets' },
  { to: '/org-chart', label: 'Directory', icon: 'ti-address-book', c: '#b45309', b: '#fff4d6', gate: 'orgChart' },
  { to: '/my-assets', label: 'My assets', icon: 'ti-device-laptop', c: '#15803d', b: '#e3f7e9' },
  /*
   * No Forms tile. Filling a form HR has assigned is a sit-down job with
   * attachments and long answers, and it was taking a slot on a nine-tile
   * grid from things people open daily. The page is still there behind the
   * menu for anyone who needs it.
   */
  { to: '/announcements', label: 'Updates', icon: 'ti-speakerphone', c: '#334e68', b: '#eef3f9' },
  { to: '/referrals', label: 'Referrals', icon: 'ti-user-plus', c: '#7c3aed', b: '#f1e9ff', perm: 'referrals' },
];

/**
 * `hidden` carries the routes this person would be redirected away from.
 *
 * Most tiles are gated by a permission, which the tile can check itself. Three
 * are gated by a role test in the router that redirects rather than refusing —
 * my-assets for admins, my-forms and referrals for HR Ops — and a tile cannot
 * see those. App.js works them out and passes them down.
 *
 * It was passing them already; this component simply never took the prop, so
 * the three tiles showed to the people the router bounces. A tile that throws
 * you back where you came from reads as broken, and these are on the home
 * screen.
 */
const MobileHome = ({ hidden = [] }) => {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const [att, setAtt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [asking, setAsking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  // How far from the nearest office fence: null until known.
  const [fence, setFence] = useState(null);
  const [geoState, setGeoState] = useState('idle');
  /*
   * The org chart is the one tile no permission covers: the API allows admins,
   * HR, and anyone who manages somebody, and the last of those is only
   * knowable by asking. Shared with the bottom bar and answered once, rather
   * than each component asking the same question on every load.
   */
  const teamSize = useTeamSize(user);

  const canAttend = can('attendance');

  useEffect(() => {
    if (!canAttend) return undefined;
    let alive = true;
    api.get('/attendance/state')
      .then(({ data }) => { if (alive) setAtt({ ...data, fetchedAt: Date.now() }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [canAttend]);

  // Tick once a second only while there is a running clock to show.
  const running = att && (att.state === 'in' || att.state === 'on_break');
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);

  // Already allowed location? Then say how far the office is without asking.
  const locateRef = useRef(null);
  const hasLocations = (att?.locations || []).length > 0;
  useEffect(() => {
    if (!hasLocations || !navigator.permissions?.query) return;
    navigator.permissions.query({ name: 'geolocation' })
      .then((p) => { if (p.state === 'granted') locateRef.current?.(false); })
      .catch(() => {});
  }, [hasLocations]);

  if (!user) return null;

  /*
   * One button, three routes.
   *
   * Office staff scan the code at the door; remote staff take a photo and have
   * their position checked; hybrid staff are asked which kind of day it is.
   * The punch itself still happens on the Attendance page — this only decides
   * which flow to open, so there is one implementation of each rather than a
   * second copy here that would drift.
   */
  const workMode = String(att?.employee?.work_mode || 'office');
  const go = (to) => { setAsking(false); navigate(to); };

  /*
   * Scanning happens here, not on the Attendance page.
   *
   * Sending somebody to that page just to open a camera meant the page drew
   * itself, the scanner covered it, and on closing it drew again before the
   * home screen came back — three transitions for one tap. The scan is a
   * single request; the only thing the page was providing was the camera.
   *
   * The selfie flow still goes there: it needs the location check, the
   * refusal states and the request-a-correction path, which are the page.
   */
  const refreshState = () => api.get('/attendance/state')
    .then(({ data }) => setAtt({ ...data, fetchedAt: Date.now() }))
    .catch(() => {});

  /*
   * Distance to the nearest registered location. Asked for only when the
   * person taps, or silently when they have already allowed location — a
   * permission prompt the moment the home screen opens is the fastest way to
   * get a "Block".
   */
  const fences = (att?.locations || []).filter((l) => l.latitude != null && l.longitude != null);
  const hasFences = fences.length > 0;
  const locate = (asked) => {
    if (!hasFences || !navigator.geolocation) return;
    if (asked) setGeoState('busy');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const here = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        let best = null;
        fences.forEach((l) => {
          const m = metresBetween(here, { lat: Number(l.latitude), lng: Number(l.longitude) });
          if (!best || m < best.metres) {
            best = { name: l.name, metres: m, inside: m <= Number(l.radius_metres || 50) };
          }
        });
        setFence(best);
        setGeoState('done');
      },
      () => setGeoState('denied'),
      { enableHighAccuracy: false, maximumAge: 60000, timeout: 10000 },
    );
  };
  locateRef.current = locate;

  const onScanned = async (text) => {
    setScanning(false);
    setResult({ status: 'working' });
    const r = await punchFromScan(text);
    setResult(r);
    if (r.status === 'done') {
      await refreshState();
      // Long enough to read, short enough not to be in the way.
      setTimeout(() => setResult(null), 2600);
    }
  };

  const startPunch = () => {
    if (workMode === 'hybrid') { setAsking(true); return; }
    if (workMode === 'remote') { go('/attendance?start=punch&from=home'); return; }
    setResult(null);
    setScanning(true);
  };

  const first = (user.name || '').split(' ')[0];
  const initials = (user.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const canSeeOrgChart = user.role === 'admin'
    || isSuperAdminPortalView(user) || isHrPortalView(user)
    || isHrAdminPortalView(user) || isHrOpsPortalView(user)
    || teamSize > 0;

  const gates = { orgChart: canSeeOrgChart };
  const tiles = TILES.filter((t) => (!t.perm || can(t.perm))
    && (!t.gate || gates[t.gate])
    && !hidden.includes(t.to));

  const day = att?.day;
  const shift = att?.shift;
  // The server recomputes the day on read, so net_minutes already counts the
  // open session up to the moment of the request; only add time since then,
  // and only while actually working (a break does not add to worked time).
  const workedMs = day ? (Number(day.net_minutes) || 0) * 60000 : 0;
  const liveMs = att?.state === 'in' && att.fetchedAt ? Math.max(0, now - att.fetchedAt) : 0;
  const requiredMin = shift ? Number(shift.required_net_hours) * 60 : null;
  const toGo = requiredMin != null && day ? requiredMin - (Number(day.net_minutes) || 0) : null;

  const stateLabel = {
    in: 'Working',
    on_break: 'On break',
    out: 'Clocked out',
  }[att?.state] || 'Not clocked in';

  const action = att?.state === 'in' || att?.state === 'on_break' ? 'out' : 'in';
  const workedMin = (workedMs + liveMs) / 60000;
  const ringPct = requiredMin ? Math.max(0, Math.min(1, workedMin / requiredMin)) : (running ? 0.04 : 0);
  const dayDone = !!requiredMin && workedMin >= requiredMin;

  return (
    <section className="mhome" aria-label="Home">
      {scanning && (
        <QrScanner
          onResult={onScanned}
          onCancel={() => setScanning(false)}
          title={att?.state === 'in' ? 'Scan to clock out' : 'Scan to clock in'}
        />
      )}

      {/*
        The outcome, over the home screen rather than on another page. A
        failure stays until dismissed — an expired code with no explanation is
        how somebody walks away believing they clocked in.
      */}
      {result && (
        <div className={`mh-punch mh-punch-${result.status}`} role="status">
          {result.status === 'working' && <span>Checking…</span>}
          {/*
            Worded from the state the server came back with, not from the state
            held here before the request.
            A repeat says so plainly. Reporting "Clocked out" for a scan that
            was ignored is how somebody walks away from the door believing their
            day has ended.
          */}
          {result.status === 'done' && (
            <span>
              {result.message || {
                in: result.repeat ? 'Already clocked in.' : 'Clocked in.',
                on_break: result.repeat ? 'Already on a break.' : 'Break started.',
                out: result.repeat ? 'Already clocked out.' : 'Clocked out.',
              }[result.state] || 'Done.'}
            </span>
          )}
          {result.status === 'failed' && (
            <>
              <span>{result.message}</span>
              <button type="button" onClick={() => setResult(null)}>Close</button>
            </>
          )}
        </div>
      )}
      <div className="mh">
        <div className="mh-top">
          <div className="mh-who" onClick={() => navigate('/profile')} role="button" tabIndex={0}>
            <div className="mh-avatar">{initials}</div>
            <div>
              <div className="mh-hello">{greeting()}</div>
              <div className="mh-name">{first}</div>
            </div>
          </div>
        </div>

        {canAttend && att?.enabled !== false && (
          <>
            {/*
              The punch button is the day: a ring that fills as the hours go
              in, green once there is enough for a full day. Tapping it is the
              same punch as before — scan, selfie or the hybrid question.
            */}
            <div className={`punch-hero${running ? ' is-running' : ''}${dayDone ? ' is-done' : ''}${action === 'out' ? ' is-out' : ''}`}>
              <button
                type="button"
                className="punch-ring"
                onClick={startPunch}
                aria-label={action === 'out' ? 'Clock out' : 'Clock in'}
              >
                <svg viewBox="0 0 220 220" aria-hidden="true">
                  <circle className="punch-track" cx="110" cy="110" r={RING_R} />
                  <circle
                    className="punch-prog"
                    cx="110" cy="110" r={RING_R}
                    strokeDasharray={RING_C}
                    strokeDashoffset={RING_C * (1 - ringPct)}
                  />
                </svg>
                <span className="punch-core">
                  <i className={`ti ${action === 'out' ? 'ti-logout-2' : 'ti-fingerprint'}`} />
                  <b>{running ? hhmmss(workedMs + liveMs) : day ? hm(day.net_minutes) : '0h 00m'}</b>
                  <em>
                    {requiredMin ? `of ${hm(requiredMin)}` : stateLabel}
                  </em>
                  <span className="punch-cta">{action === 'out' ? 'Tap to clock out' : 'Tap to clock in'}</span>
                </span>
              </button>
              <div className="punch-meta">
                <span className="clock-state">
                  <span className={`att-dot ${att?.state === 'in' ? 'att-dot-in' : att?.state === 'on_break' ? 'att-dot-break' : ''}`} />
                  {dayDone ? 'Full day done' : stateLabel}{running && att.since ? ` · since ${clock(att.since)}` : ''}
                </span>
                <span className="punch-shift">
                  {shift ? `${shift.name} · ${shortTime(shift.start_time)}–${shortTime(shift.end_time)}` : 'No shift assigned'}
                </span>
                {fence && (
                  <span className={`punch-fence ${fence.inside ? 'in' : 'out'}`}>
                    <i className={`ti ${fence.inside ? 'ti-map-pin-check' : 'ti-map-pin'}`} />
                    {fence.inside ? `At ${fence.name}` : `You're ${fenceDist(fence.metres)} from ${fence.name}`}
                  </span>
                )}
                {!fence && hasFences && geoState !== 'denied' && (
                  <button type="button" className="punch-fence ask" onClick={() => locate(true)}>
                    <i className="ti ti-current-location" /> {geoState === 'busy' ? 'Finding you…' : 'How far is the office?'}
                  </button>
                )}
              </div>
            </div>
            {/*
              Hybrid staff are asked, because a hybrid week genuinely varies
              and guessing wrong sends somebody the long way round every other
              day. Office and remote never see this — their answer is known.
            */}
            {asking && (
              <div className="clock-choice" role="group" aria-label="How are you working today">
                <button type="button" className="clock-choice-btn"
                  onClick={() => { setAsking(false); setResult(null); setScanning(true); }}>
                  <strong>I am at the office</strong>
                  <span>Scan the code on the screen</span>
                </button>
                <button type="button" className="clock-choice-btn"
                  onClick={() => go('/attendance?start=punch&from=home')}>
                  <strong>I am working from home</strong>
                  <span>Photo and location check</span>
                </button>
                <button type="button" className="clock-choice-cancel"
                  onClick={() => setAsking(false)}>Cancel</button>
              </div>
            )}

            <div className="clock-mini">
              <div><b>{clock(day?.first_in)}</b><span>In</span></div>
              <div><b>{day ? hm(day.break_minutes) : '--'}</b><span>Break</span></div>
              <div><b>{toGo != null ? (toGo > 0 ? hm(toGo) : 'Done') : '--'}</b><span>To go</span></div>
            </div>
          </>
        )}
      </div>

      <div className="mh-body">
        <div className="mh-sec">Quick access</div>
        <div className="launch">
          {tiles.map((t) => (
            <button key={t.to} type="button" className="tile" onClick={() => navigate(t.to)}>
              <span className="tile-ic" style={{ '--c': t.c, '--b': t.b }}><i className={`ti ${t.icon}`} /></span>
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MobileHome;
